import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLoad } from "@/hooks/useData";
import { api } from "@/lib/api";
import { safeOptions } from "@/components/Filters";
import { cn } from "@/lib/utils";

export const useRetMeta = () => useLoad(() => api.get("/returns/meta").then((r) => r.data)).data;

const TONE = (s) => s.startsWith("Closed") ? "bg-emerald-50 text-emerald-700 border-emerald-200"
  : s === "Unmatched" || s === "Refund Rejected" ? "bg-red-50 text-red-700 border-red-200"
  : s.startsWith("Claim") ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-amber-50 text-amber-700 border-amber-200";

export const RetPill = ({ status }) => (
  <span className={cn("inline-block whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold", TONE(status || ""))} data-testid="ret-status-pill">{status}</span>
);

export const DaysCell = ({ days, amber = 7, red = 15 }) => days == null ? <span className="text-muted-foreground">—</span>
  : <span className={cn("font-mono font-bold", days > red ? "text-red-600" : days > amber ? "text-amber-600" : "")}>{days}</span>;

export const WarnChips = ({ list = [] }) => (
  <div className="flex flex-wrap gap-1">{list.map((w) => <span key={w} className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">{w}</span>)}</div>
);

export const RetLink = ({ r }) => <Link to={`/returns/r/${r.id}`} className="font-mono text-xs font-semibold text-primary hover:underline" data-testid={`ret-link-${r.return_id}`}>{r.return_id}</Link>;

export const orderOf = (r) => r.amazon_order_id || r.website_invoice_no || "";

export const opts = (meta, key, current) => {
  const list = meta?.settings?.lists?.[key] || [];
  return list.filter((o) => o.active || o.value === current).map((o) => o.value);
};

export const Fld = ({ label, children, className }) => (
  <div className={cn("space-y-1", className)}><Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);

export const Sel = ({ value, onChange, options, testid, placeholder = "Select…", disabled }) => (
  <Select value={value || "__none"} onValueChange={(v) => onChange(v === "__none" ? "" : v)} disabled={disabled}>
    <SelectTrigger className="h-9" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="__none">—</SelectItem>
      {safeOptions((options || []).map((o) => [o, o])).map(([o]) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const Txt = ({ value, onChange, testid, type = "text", ...rest }) => (
  <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-9" data-testid={testid} {...rest} />
);

export const Card = ({ title, children, testid, right }) => (
  <div className="panel p-4" data-testid={testid}>
    <div className="mb-3 flex items-center justify-between"><h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{title}</h3>{right}</div>
    {children}
  </div>
);
